import { defineStore } from 'pinia';

import {
  AutocompleteRepository,
  GiftTicketAutocompleteDto,
} from '@/repositories/generated';

/** GiftTicketSelectで扱うID/名称ペア。APIのidはnumberのため文字列へ正規化して保持する */
export type GiftTicketAutocompleteItem = { id: string; name: string };

type GiftTicketAutocompleteRequestForm = {
  term: string | undefined;
  giftBusinessId: number | undefined;
};

type GiftTicketAutocompleteStoreState = {
  searchCache: Record<string, GiftTicketAutocompleteItem>;
};

const toItems = (res: GiftTicketAutocompleteDto[]): GiftTicketAutocompleteItem[] => res
  .filter((it): it is { id: number; name: string } => it.id != null && it.name != null)
  .map((it) => ({ id: String(it.id), name: it.name }));

/**
 * 検索候補表示コンポーネント用にレスポンスをキャッシュするストア
 */
export const useGiftTicketAutocompleteStore = defineStore('giftTicketAutocomplete', {
  state: (): GiftTicketAutocompleteStoreState => ({
    searchCache: {},
  }),
  getters: {
    // idを元にキャッシュから取得するcomputed。なければ自動補完APIを非同期で叩く
    getByIdOrDummy: (state) => (id: string): GiftTicketAutocompleteItem => {
      const cache = state.searchCache[id];
      if (cache) {
        return cache;
      }

      // 自動補完APIが１度だけ叩かれるように、キャッシュにダミーデータを差し込んでおく
      const dummyItem = { id, name: '' } as GiftTicketAutocompleteItem;
      state.searchCache[id] = dummyItem;
      (async () => {
        // 商品券面はIDのみで補完可能（商品券事業未選択でも復元できる）
        const res = await AutocompleteRepository.searchGiftTicket({ term: id, giftBusinessId: undefined });
        toItems(res).forEach((it) => {
          state.searchCache[it.id] = it;
        });
      })();
      return dummyItem;
    },
  },
  actions: {
    async search(form: GiftTicketAutocompleteRequestForm): Promise<GiftTicketAutocompleteItem[]> {
      const res = await AutocompleteRepository.searchGiftTicket(form);
      const items = toItems(res);
      items.forEach((it) => {
        this.searchCache[it.id] = it;
      });
      return items;
    },
  },
});
